import { Link } from "react-router-dom";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { MetaList } from "@/profitability/meta-list";
import { inputTitle, useReportInputs } from "@/profitability/missing-value";
import { shortPeriod } from "@/profitability/new-run/run-period";
import type { DocumentLinks } from "./links";
import type { DocumentRunRef } from "./types";

const periodName = (run: DocumentRunRef) =>
  run.start_date && run.end_date
    ? shortPeriod({ start_date: run.start_date, end_date: run.end_date })
    : "Documents only";

/**
 * One line per period: a period's reruns read the same file, so the run
 * listed is the one on screen when it is among them, else the latest.
 */
export const periodsReading = (
  runs: DocumentRunRef[],
  currentRunId: string,
) => {
  const byPeriod = new Map<string, DocumentRunRef>();
  for (const run of runs) {
    const key = [run.report_id, run.start_date, run.end_date].join(":");
    const kept = byPeriod.get(key);
    if (!kept || run.run_id === currentRunId) byPeriod.set(key, run);
  }
  return [...byPeriod.values()];
};

/**
 * The periods that read this same file, latest first: one file can stand
 * behind several periods, and a rerun reads its period's file again. Each
 * opens that period's Documents tab on the file.
 */
export const ReadIn = ({
  runs,
  loading,
  currentRunId,
  documentId,
  links,
}: {
  runs: DocumentRunRef[] | undefined;
  loading: boolean;
  currentRunId: string;
  documentId: string;
  links: DocumentLinks;
}) => {
  const inputs = useReportInputs();
  return (
    <Stack gap={2} align="stretch">
      <Text variant="title" render={<h3 />}>
        Read in
      </Text>
      {loading ? (
        <Skeleton className="h-10 w-full" />
      ) : !runs?.length ? (
        <Text tone="muted">No period has read this file yet.</Text>
      ) : (
        <Stack render={<ul />} gap={1} align="stretch">
          {periodsReading(runs, currentRunId).map((run) => {
            const href =
              run.run_id === currentRunId
                ? null
                : links.period(run.run_id, run.report_id, documentId);
            const name = periodName(run);
            return (
              <li key={run.run_id}>
                <Text>
                  <MetaList>
                    {href ? (
                      <Link
                        to={href}
                        className="text-link underline-offset-2 hover:underline"
                      >
                        {name}
                      </Link>
                    ) : (
                      <span className="font-medium">{name}</span>
                    )}
                    {run.run_id === currentRunId && (
                      <span className="text-subtle">This period</span>
                    )}
                    <span className="text-subtle">
                      {run.role === "goal"
                        ? "As your numbers"
                        : `As ${inputTitle(inputs, run.input_key) ?? run.input_key ?? "a file it read"}`}
                    </span>
                  </MetaList>
                </Text>
              </li>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};
